import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  Layers,
  Sparkles,
  CheckCircle2,
  Info,
  Store
} from 'lucide-react';

export interface VendorStats {
  vendor_id: string;
  vendor_name: string;
  invoices_processed: number;
  clusters_identified: number;
  total_line_items: number;
  total_amount: number;
}

export interface OverallStats {
  totalVendors: number;
  totalInvoices: number;
  totalClusters: number;
  totalLineItems: number;
  totalAmount: number;
  vendorStats: VendorStats[];
}

interface VendorInvoiceSummaryDisplayProps {
  stats: OverallStats;
  showGuidelines?: boolean;
}

export const VendorInvoiceSummaryDisplay = ({ stats, showGuidelines = true }: VendorInvoiceSummaryDisplayProps) => {
  return (
    <div className="space-y-6">
      {/* Prominent Header Banner */}
      <Card className="border-2 border-primary bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Invoice Mapping Summary</h2>
              <p className="text-muted-foreground">
                Before you begin mapping, review the insights below and follow the training guidelines to efficiently map your invoice items to menu ingredients.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Ready to Map Your Invoice Items</h3>
        <p className="text-muted-foreground">
          We've processed your invoices and identified product clusters. Review the summary below, then proceed to map them to your menu ingredients.
        </p>
      </div>

      {/* Training Guidelines Card - Prominent */}
      {showGuidelines && (
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Training Guidelines & Instructions
            </CardTitle>
            <CardDescription>
              Follow these steps to efficiently map your invoice items to menu ingredients
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  1
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Review Product Clusters</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    We've automatically identified {stats.totalClusters} product clusters from {stats.totalLineItems} line items across {stats.totalInvoices} invoices. Each cluster groups similar items (e.g., "Chicken Breast" appearing on multiple invoices).
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  2
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Map Clusters to Ingredients</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    For each cluster, select the corresponding menu ingredient from your system. Once mapped, all line items in that cluster will be automatically associated with that ingredient.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  3
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Enable Auto-Matching</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    After mapping, future invoices from the same vendor will automatically match these items to the correct ingredients, saving you time on data entry.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  4
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Track Price Trends</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Once mapped, we'll automatically track price changes over time for each ingredient, helping you monitor cost fluctuations and negotiate better deals.
                  </p>
                </div>
              </div>
            </div>
            
            <Alert className="bg-amber-50 border-amber-200 mt-4">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                <strong>Pro Tip:</strong> Start with the largest clusters first - they represent your most frequently purchased items and will have the biggest impact on your cost tracking.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Overall Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Store className="h-4 w-4" />
              Vendors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalVendors}</div>
            <p className="text-xs text-muted-foreground mt-1">
              vendor{stats.totalVendors !== 1 ? 's' : ''} processed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalInvoices}</div>
            <p className="text-xs text-muted-foreground mt-1">
              invoice{stats.totalInvoices !== 1 ? 's' : ''} processed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Clusters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalClusters}</div>
            <p className="text-xs text-muted-foreground mt-1">
              product cluster{stats.totalClusters !== 1 ? 's' : ''} identified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Line Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalLineItems}</div>
            <p className="text-xs text-muted-foreground mt-1">
              item{stats.totalLineItems !== 1 ? 's' : ''} extracted
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Total Amount Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Invoice Amount</p>
              <p className="text-3xl font-bold text-primary mt-1">
                ${stats.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendor Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            Vendor Breakdown
          </CardTitle>
          <CardDescription>
            Detailed view of processed invoices by vendor
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {stats.vendorStats.map(vendor => (
            <div key={vendor.vendor_id} className="p-4 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{vendor.vendor_name}</h4>
                <Badge variant="outline">
                  {vendor.invoices_processed} invoice{vendor.invoices_processed !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Clusters</p>
                  <p className="font-semibold">{vendor.clusters_identified}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Line Items</p>
                  <p className="font-semibold">{vendor.total_line_items}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Amount</p>
                  <p className="font-semibold">
                    ${vendor.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Cluster Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Cluster Analysis
          </CardTitle>
          <CardDescription>
            Summary of identified product clusters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total clusters to map</span>
              <span className="font-semibold">{stats.totalClusters}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Average line items per cluster</span>
              <span className="font-semibold">
                {stats.totalClusters > 0 
                  ? (stats.totalLineItems / stats.totalClusters).toFixed(1)
                  : '0'}
              </span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 inline mr-1" />
                By mapping {stats.totalClusters} clusters, you'll enable automatic matching for all {stats.totalLineItems} line items!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
